// src/components/ProjectManagement/EditProjectModal.jsx
import React, { useState } from 'react';
import { db } from '../../firebase/config';
import { doc, updateDoc, addDoc, collection, Timestamp } from 'firebase/firestore';

const EditProjectModal = ({ project, currentUser, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    projectName: project.projectName || '',
    description: project.description || '',
    productType: project.productType || 'custom',
    startDate: project.startDate?.toDate?.()?.toISOString().split('T')[0] || '',
    endDate: project.endDate?.toDate?.()?.toISOString().split('T')[0] || ''
  });
  
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.projectName.trim()) newErrors.projectName = 'Project name is required';
    if (!formData.endDate) newErrors.endDate = 'End date is required';
    if (formData.startDate && formData.endDate) {
      const start = new Date(formData.startDate);
      const end = new Date(formData.endDate);
      if (start > end) newErrors.endDate = 'End date must be after start date';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    
    setLoading(true);
    try {
      const updateData = {
        projectName: formData.projectName,
        description: formData.description,
        productType: formData.productType,
        startDate: formData.startDate ? Timestamp.fromDate(new Date(formData.startDate)) : null,
        endDate: Timestamp.fromDate(new Date(formData.endDate)),
        updatedAt: Timestamp.now(),
        updatedBy: currentUser.uid,
        updatedByName: currentUser.displayName || currentUser.email?.split('@')[0]
      };
      
      await updateDoc(doc(db, 'projects', project.id), updateData);
      
      // Add to project history/audit log
      await addDoc(collection(db, 'projectHistory'), {
        projectId: project.id,
        projectName: project.projectName,
        action: 'project_updated',
        changes: {
          projectName: formData.projectName,
          description: formData.description,
          productType: formData.productType,
          startDate: formData.startDate,
          endDate: formData.endDate
        },
        performedBy: currentUser.uid,
        performedByName: currentUser.displayName || currentUser.email?.split('@')[0],
        timestamp: Timestamp.now()
      });
      
      alert('✅ Project updated successfully!');
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Error updating project:', error);
      alert('Failed to update project');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" style={{ maxWidth: '550px' }} onClick={(e) => e.stopPropagation()}>
        <h3>✏️ Edit Project</h3>
        <p style={{ color: '#666', marginBottom: '20px' }}>
          Update project details for <strong>{project.projectName}</strong>
        </p>
        
        <form onSubmit={handleSubmit}>
          {/* Project Name */}
          <div style={{ marginBottom: '20px' }}>
            <label>Project Name *</label>
            <input
              type="text"
              name="projectName"
              value={formData.projectName}
              onChange={handleChange}
              style={{ 
                width: '100%', 
                padding: '10px', 
                borderRadius: '8px', 
                border: `2px solid ${errors.projectName ? '#ef4444' : '#e5e7eb'}`,
                fontSize: '14px'
              }}
            />
            {errors.projectName && <small style={{ color: '#ef4444', marginTop: '4px', display: 'block' }}>{errors.projectName}</small>}
          </div>
          
          {/* Description */}
          <div style={{ marginBottom: '20px' }}>
            <label>Description</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows="3"
              placeholder="Project description..."
              style={{ 
                width: '100%', 
                padding: '10px', 
                borderRadius: '8px', 
                border: '2px solid #e5e7eb',
                fontSize: '14px',
                fontFamily: 'inherit'
              }}
            />
          </div>
          
          {/* Product Type */}
          <div style={{ marginBottom: '20px' }}>
            <label>Product Type</label>
            <select
              name="productType"
              value={formData.productType}
              onChange={handleChange}
              style={{ 
                width: '100%', 
                padding: '10px', 
                borderRadius: '8px', 
                border: '2px solid #e5e7eb',
                fontSize: '14px'
              }}
            >
              <option value="custom">🛠️ Custom</option>
              <option value="standard">📦 Standard</option>
              <option value="premium">💎 Premium</option>
            </select>
          </div>
          
          {/* Dates */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
            <div>
              <label>Start Date</label>
              <input
                type="date"
                name="startDate"
                value={formData.startDate}
                onChange={handleChange}
                style={{ 
                  width: '100%', 
                  padding: '10px', 
                  borderRadius: '8px', 
                  border: '2px solid #e5e7eb',
                  fontSize: '14px'
                }}
              />
            </div>
            <div>
              <label>End Date *</label>
              <input
                type="date"
                name="endDate"
                value={formData.endDate}
                onChange={handleChange}
                min={formData.startDate || new Date().toISOString().split('T')[0]}
                style={{ 
                  width: '100%', 
                  padding: '10px', 
                  borderRadius: '8px', 
                  border: `2px solid ${errors.endDate ? '#ef4444' : '#e5e7eb'}`,
                  fontSize: '14px'
                }}
              />
              {errors.endDate && <small style={{ color: '#ef4444', marginTop: '4px', display: 'block' }}>{errors.endDate}</small>}
            </div>
          </div>
          
          {/* Current Values Summary */}
          <div style={{ 
            background: '#f5f5f5', 
            padding: '12px', 
            borderRadius: '8px', 
            marginBottom: '20px',
            fontSize: '12px',
            color: '#666'
          }}>
            <strong>Current Values:</strong>
            <div style={{ marginTop: '8px' }}>
              <div>📋 Project: {project.projectName}</div>
              <div>📅 Start: {project.startDate?.toDate?.()?.toLocaleDateString() || 'Not set'}</div>
              <div>🏁 End: {project.endDate?.toDate?.()?.toLocaleDateString() || 'Not set'}</div>
              <div>📦 Type: {project.productType || 'custom'}</div>
            </div>
          </div>
          
          {/* Form Actions */}
          <div className="modal-actions" style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
            <button 
              type="submit" 
              className="assign-btn" 
              disabled={loading}
              style={{ flex: 1, padding: '12px' }}
            >
              {loading ? 'Saving...' : '✓ Save Changes'}
            </button>
            <button 
              type="button" 
              onClick={onClose} 
              className="cancel-btn"
              style={{ flex: 1, padding: '12px' }}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditProjectModal;